/*
 * The Diagnostics tab: what is broken across the org, and a report you can
 * hand to a model.
 *
 * The copy button is the feature. Everything on this page is also readable on
 * screen, but the thing people actually want when an environment goes red is
 * the whole picture in one paste: the failing URL, what it returned in its own
 * words, which branch feeds it, whether CI passed, and what the surrounding
 * project looks like. Assembling that by hand from four panels is the work
 * this removes.
 *
 * The sweep is not run on mount. It probes every environment every repository
 * in the org declares, which is real traffic to real deployments, so it waits
 * to be asked.
 */
import { useState } from 'react'
import { getDatabases, getOrgHealth } from '../lib/api'
import { copyText, isFailing, orgDiagnostics, rollup } from '../lib/diagnostics'
import { ApiError, type DatabasesResponse, type EnvironmentHealth, type OrgHealth } from '../types'
import { CheckCircle, Clipboard, ExternalLink, Sync, Warning, XCircle } from './Icons'

function StatusPill({ e }: { e: EnvironmentHealth }) {
  const tone = e.status === 'up' ? 'success' : e.status === 'protected' || e.status === 'blocked' ? 'pending' : 'failure'
  return (
    <span className={`db-status db-status-${tone}`}>
      {e.status}
      {e.httpStatus ? ` ${e.httpStatus}` : ''}
    </span>
  )
}

function EnvRow({ e }: { e: EnvironmentHealth }) {
  const bad = isFailing(e)
  return (
    <div className={`diag-row ${bad ? 'bad' : ''}`}>
      <div className="diag-row-head">
        <span className="diag-env">{e.env}</span>
        <a className="diag-url" href={e.url} target="_blank" rel="noreferrer noopener" title={e.url}>
          {e.url.replace(/^https:\/\//, '')}
          <ExternalLink size={10} />
        </a>
        <span className="diag-ms">{e.ms !== null ? `${e.ms}ms` : ''}</span>
        <StatusPill e={e} />
      </div>
      {e.error ? (
        <div className="diag-error">
          <Warning size={12} />
          <span>
            {e.error}
            {e.errorKind ? <span className="diag-kind">{e.errorKind}</span> : null}
          </span>
        </div>
      ) : null}
      {e.finalUrl ? <div className="diag-note">redirected {e.redirects}x to {e.finalUrl}</div> : null}
      {e.body ? <pre className="diag-body">{e.body}</pre> : null}
    </div>
  )
}

export function Diagnostics() {
  const [health, setHealth] = useState<OrgHealth | null>(null)
  const [databases, setDatabases] = useState<DatabasesResponse | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const run = async () => {
    setRunning(true)
    setError(null)
    setCopied(false)
    try {
      // Databases are cheap and part of the report; failing to read them must
      // not lose the health sweep, which is the expensive half.
      const [h, d] = await Promise.all([getOrgHealth(), getDatabases().catch(() => null)])
      setHealth(h)
      setDatabases(d)
    } catch (err) {
      setError((err as ApiError).message)
    } finally {
      setRunning(false)
    }
  }

  const copy = async () => {
    if (!health) return
    const ok = await copyText(orgDiagnostics(health, databases))
    setCopied(ok)
    if (ok) setTimeout(() => setCopied(false), 2500)
  }

  const all = health?.repos.flatMap(r => r.environments) ?? []
  const failing = all.filter(isFailing)
  const verdict = rollup(all)

  return (
    <div className="shell" style={{ paddingTop: 8 }}>
      <section className="section" style={{ marginTop: 0 }}>
        <div className="section-label">
          <span>Environment health</span>
          {health ? (
            <span className="count">
              · {all.length} probed across {health.repos.length} repo{health.repos.length === 1 ? '' : 's'}
            </span>
          ) : null}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {health ? (
              <button type="button" className="btn btn-ghost" style={{ height: 26, padding: '0 10px' }} onClick={copy}>
                <Clipboard size={13} />
                {copied ? 'Copied' : 'Copy report'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              style={{ height: 26, padding: '0 10px' }}
              onClick={run}
              disabled={running}
            >
              <Sync size={13} className={running ? 'spin' : undefined} />
              {running ? 'Probing…' : health ? 'Re-run' : 'Run check'}
            </button>
          </div>
        </div>

        {error ? (
          <div className="banner">
            <Warning size={16} />
            <span className="body">{error}</span>
          </div>
        ) : !health ? (
          <div className="empty">
            {running
              ? 'Probing every environment this organization declares…'
              : 'Nothing has been probed yet. Run check fetches every ' +
                'repository’s .github/branch-viz.json and requests each environment URL it declares, then builds a ' +
                'report you can paste into Claude or any other model.'}
          </div>
        ) : (
          <>
            <div className={`diag-verdict diag-verdict-${verdict}`}>
              {verdict === 'ok' ? <CheckCircle size={16} /> : <XCircle size={16} />}
              <span>
                {verdict === 'ok'
                  ? `All ${all.length} environments answered.`
                  : `${failing.length} of ${all.length} environments failing.`}
              </span>
              <span className="diag-verdict-meta">
                scanned {health.scanned} repo{health.scanned === 1 ? '' : 's'} in {health.tookMs}ms
                {health.truncated ? ' · list truncated' : ''}
              </span>
            </div>

            {health.repos.map(r => (
              <div className="diag-repo" key={r.repo}>
                <div className="diag-repo-name">
                  {r.repo}
                  {r.error ? <span className="diag-kind">{r.error}</span> : null}
                </div>
                {r.environments.map(e => (
                  <EnvRow e={e} key={`${r.repo}:${e.env}`} />
                ))}
              </div>
            ))}
          </>
        )}
      </section>

      <section className="section">
        <div className="section-label">What the report contains</div>
        <div className="diag-explainer">
          <p>
            Every probed URL with its result, latency, and up to 600 characters of a failing response’s own text.
            Then, for the repository-scoped version on the Repository tab: the environment-to-branch mapping, CI state
            on each environment branch, promotion state between them, and any open pull request that cannot merge.
            The org-scoped version adds the Azure database inventory.
          </p>
          <p>
            Databases are <strong>not</strong> connection-tested. Both Tribunal servers admit a single IP by firewall
            rule and this dashboard is not on it, so a connection attempt from here would measure the firewall, not the
            database. What the report carries instead is what Azure Resource Manager states: provisioning state,
            version, network posture, backup retention.
          </p>
        </div>
      </section>
    </div>
  )
}
